import { Theme, TypographyVariantsOptions } from '@mui/material/styles';

// Extend the TypographyVariants interface to include custom variants
declare module '@mui/material/styles' {
  interface TypographyVariants {
    cardDescription: React.CSSProperties;
    customInput: React.CSSProperties;
    mainContent: React.CSSProperties;
    menuCaption: React.CSSProperties;
    subMenuCaption: React.CSSProperties;
    commonAvatar: React.CSSProperties;
    smallAvatar: React.CSSProperties;
    mediumAvatar: React.CSSProperties;
    largeAvatar: React.CSSProperties;
    warehouseMonospace_h3: React.CSSProperties;
    warehouseMonospace_h4: React.CSSProperties;
    warehouseMonospace_h5: React.CSSProperties;
    warehouseMonospace_caption: React.CSSProperties;
  }

  interface TypographyVariantsOptions {
    cardDescription?: React.CSSProperties;
    customInput?: React.CSSProperties;
    mainContent?: React.CSSProperties;
    menuCaption?: React.CSSProperties;
    subMenuCaption?: React.CSSProperties;
    commonAvatar?: React.CSSProperties;
    smallAvatar?: React.CSSProperties;
    mediumAvatar?: React.CSSProperties;
    largeAvatar?: React.CSSProperties;
    warehouseMonospace_h3?: React.CSSProperties;
    warehouseMonospace_h4?: React.CSSProperties;
    warehouseMonospace_h5?: React.CSSProperties;
    warehouseMonospace_caption?: React.CSSProperties;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    warehouseMonospace_h3: true;
    warehouseMonospace_h4: true;
    warehouseMonospace_h5: true;
    warehouseMonospace_caption: true;
  }
}

const Typography = (
  theme: Theme,
  borderRadius: number,
  fontFamily: string
): TypographyVariantsOptions => ({
  fontFamily,
  h6: {
    fontWeight: 500,
    color: theme.palette.grey[600],
    fontSize: '0.75rem',
  },
  h5: {
    fontSize: '0.875rem',
    color: theme.palette.grey[600],
    fontWeight: 500,
  },
  h4: {
    fontSize: '1rem',
    color: theme.palette.grey[600],
    fontWeight: 600,
  },
  h3: {
    fontSize: '1.25rem',
    color: theme.palette.grey[600],
    fontWeight: 600,
  },
  h2: {
    fontSize: '1.5rem',
    color: theme.palette.grey[600],
    fontWeight: 700,
  },
  h1: {
    fontSize: '2.125rem',
    color: theme.palette.grey[600],
    fontWeight: 700,
  },
  subtitle1: {
    fontSize: '0.875rem',
    fontWeight: 500,
    color: theme.palette.text.dark,
  },
  subtitle2: {
    fontSize: '0.75rem',
    fontWeight: 400,
    color: theme.palette.text.secondary,
  },
  cardDescription: {
    fontSize: '14px',
    fontWeight: 500,
    color: theme.palette.grey[500],
    lineHeight: '20px',
  },
  caption: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    fontWeight: 400,
  },
  body1: {
    fontSize: '0.875rem',
    fontWeight: 400,
    lineHeight: '1.334em',
  },
  body2: {
    letterSpacing: '0em',
    fontWeight: 400,
    lineHeight: '1.5em',
    color: theme.palette.text.primary,
  },
  button: {
    textTransform: 'capitalize',
  },
  warehouseMonospace_h3: {
    fontFamily: `'AtkinsonHyperlegibleMono', 'Inter', 'Roboto', 'monospace'`,
    fontSize: '1.25rem',
    fontWeight: 600,
    letterSpacing: '0.02em',
    lineHeight: '1.1',
  },
  warehouseMonospace_h4: {
    fontFamily: `'AtkinsonHyperlegibleMono', 'Inter', 'Roboto', 'monospace'`,
    fontSize: '1.125rem',
    fontWeight: 600,
    letterSpacing: '0.02em',
    lineHeight: '1.1',
  },
  warehouseMonospace_h5: {
    fontFamily: `'AtkinsonHyperlegibleMono', 'Inter', 'Roboto', 'monospace'`,
    fontSize: '1rem',
    fontWeight: 600,
    letterSpacing: '0.02em',
    lineHeight: '1.1',
  },
  warehouseMonospace_caption: {
    fontFamily: `'AtkinsonHyperlegibleMono', 'Inter', 'Roboto', 'monospace'`,
    fontSize: '0.75rem',
    fontWeight: 400,
    letterSpacing: '0.02em',
    lineHeight: '1.1',
  },
  customInput: {
    marginTop: 1,
    marginBottom: 1,
    // @ts-expect-error - there is incorrect typing for the label selector in MUI
    '& > label': {
      top: 23,
      left: 0,
      color: theme.palette.grey[500],
      '&[data-shrink="false"]': {
        top: 5,
      },
    },
    '& > div > input': {
      padding: '30.5px 14px 11.5px !important',
    },
    '& legend': {
      display: 'none',
    },
    '& fieldset': {
      top: 0,
    },
  },
  mainContent: {
    backgroundColor: theme.palette.brand.magnesium,
    width: '100%',
    minHeight: '100vh',
    flexGrow: 1,
    padding: '20px',
    marginLeft: '20px',
    borderRadius: `0px`,
  },
  menuCaption: {
    fontSize: '0.875rem',
    fontWeight: 500,
    color: theme.palette.grey[600],
    padding: '6px',
    textTransform: 'capitalize',
    marginTop: '10px',
  },
  subMenuCaption: {
    fontSize: '0.6875rem',
    fontWeight: 500,
    color: theme.palette.text.secondary,
    textTransform: 'capitalize',
  },
  commonAvatar: {
    cursor: 'pointer',
    borderRadius: '8px',
  },
  smallAvatar: {
    width: '22px',
    height: '22px',
    fontSize: '0.8rem',
  },
  mediumAvatar: {
    width: '34px',
    height: '34px',
    fontSize: '1.0rem',
  },
  largeAvatar: {
    width: '44px',
    height: '44px',
    fontSize: '1.2rem',
  },
});

export default Typography;
